import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageProvider";
import { isPaymentUiBlocked, isNativeIapEnabled } from "@/lib/platform";
import { analytics } from "@/lib/analytics";
import { useCredits } from "@/hooks/useCredits";
import { unitsToCredits } from "@/config/credits";
import IosPaywall from "@/components/paywall/IosPaywall";
import IosCreditSheet from "@/components/paywall/IosCreditSheet";
import { localeTagFor, type Lang } from "@/i18n/locale";
import type { PlanTier } from "@/lib/quotaError";

/** "2026-10-01T00:00:00.000Z" → "1 de outubro" no locale da conta; inválido → "". */
export function formatResetDate(periodEndIso: string, lang: Lang): string {
  const d = new Date(periodEndIso);
  if (!Number.isFinite(d.getTime())) return "";
  try {
    return d.toLocaleDateString(localeTagFor(lang), { day: "numeric", month: "long" });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

export type PaywallVariant = "module_locked" | "quota_exceeded" | "voice_cap" | "generic";

/**
 * Module IDs with bespoke per-module copy. Other module strings (e.g.
 * "insights", "language_speaking") fall back to the generic module copy.
 */
export type LockedModuleId = "brain" | "fitness" | "nutrition";
const KNOWN_MODULES = new Set<string>(["brain", "fitness", "nutrition"]);

// "image" (F2): cap mensal de imagens — copy própria em paywall.quota.image
// (monthly-only; imagem não tem cap diário).
// "stt" (06/08): cota mensal de transcrição. Copy própria em
// paywall.quota.stt nos 6 locales — cair no genérico de "text" diria "suas
// mensagens acabaram" para quem acabou de falar no microfone.
export type QuotaKind = "text" | "tts" | "live" | "image" | "stt" | "email" | "files";

interface PaywallSheetProps {
  open: boolean;
  onClose: () => void;
  variant: PaywallVariant;
  /** Used when variant === "module_locked". Unknown modules → generic copy. */
  moduleId?: string;
  /** Used when variant === "quota_exceeded" to pick kind-specific copy. */
  quotaKind?: QuotaKind;
  /**
   * Used when variant === "quota_exceeded": "daily" swaps in the daily-cap
   * copy + dominant "Assinar Premium" CTA (the daily text limit is the peak-
   * intent conversion moment). Absent/monthly keeps the original copy.
   */
  quotaScope?: "daily" | "monthly";
  /**
   * Analytics attribution for the paywall_cta_click / paywall_dismissed
   * outcome split (e.g. "daily_limit"). Falls back to the variant name.
   */
  placement?: string;
  /** ISO do fim do período (period_end do 402) — copy "renova em {{date}}" do LIVE. */
  periodEnd?: string;
  /** Tier de quem bateu o teto. Premium sem minutos de LIVE não recebe upsell de Premium. */
  currentTier?: PlanTier;
}

/**
 * Plans V3.2 paywall surface. Phase 1 ships the `module_locked` variant
 * with per-module copy. Other variants render shell-only stubs so the
 * component can be slotted into future flows without churn:
 *   - voice_cap → cap-reached upsell (Phase 2)
 *   - generic  → fallback / unattributed upsell (Phase 2+)
 *
 * Unlock CTA navigates to the existing /pricing route. Phase 3 replaces
 * the navigate() with a Capacitor Browser open() to aivyv.com/pricing
 * for native checkout (out of scope here).
 */
export function PaywallSheet({
  open,
  onClose,
  variant,
  moduleId,
  quotaKind,
  quotaScope,
  placement,
  periodEnd,
  currentTier,
}: PaywallSheetProps) {
  const { t, tf, lang } = useLanguage();
  const navigate = useNavigate();
  const { balanceUnits: creditBalanceUnits, refresh: refreshCredits } = useCredits();
  const [iapOpen, setIapOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);

  // Analytics placement for the shown → cta_click | dismissed funnel.
  const analyticsPlacement = placement ?? variant;
  // Daily text-cap variant of the quota paywall (peak-intent moment).
  const isDailyQuota = variant === "quota_exceeded" && quotaScope === "daily";
  // LIVE (LOTE 3): Premium sem minutos não tem o que comprar — Pro é "em
  // breve" e crédito NÃO vale para o LIVE (decisão do LOTE 1: TTS_UNITS_PER_
  // SECOND precifica ElevenLabs, não Realtime). Diz quando renova e só.
  const livePremiumOut =
    variant === "quota_exceeded" && quotaKind === "live" && currentTier !== undefined && currentTier !== "free";
  // Crédito não cobre o LIVE em tier nenhum: esconder o CTA evita vender
  // um pacote que não destrava o que o usuário acabou de tentar usar.
  const creditsApply = quotaKind !== "live" && quotaKind !== "email" && quotaKind !== "files";

  // Per-variant copy resolution.
  let title = t("paywall.moduleLocked.generic.title");
  let description = t("paywall.moduleLocked.generic.description");

  if (variant === "module_locked" && moduleId && KNOWN_MODULES.has(moduleId)) {
    title = t(`paywall.moduleLocked.${moduleId}.title`);
    description = t(`paywall.moduleLocked.${moduleId}.description`);
  } else if (isDailyQuota) {
    // Daily cap hit — full Premium upsell instead of the old soft toast.
    // Daily copy exists for the text kind (the only daily-scoped cap today);
    // a non-text daily 402 falls back to the kind-specific monthly copy.
    title = t("paywall.quota.daily.title");
    description = quotaKind && quotaKind !== "text"
      ? t("paywall.quota." + quotaKind)
      : t("paywall.quota.daily.text");
  } else if (livePremiumOut) {
    title = t("paywall.quota.live.premium.title");
    const date = periodEnd ? formatResetDate(periodEnd, lang) : "";
    description = date
      ? tf("paywall.quota.live.premium.description", { date })
      : t("paywall.quota.live.premium.descriptionNoDate");
  } else if (variant === "quota_exceeded") {
    // Monthly cap hit — the primary Free→Premium conversion moment.
    title = t("paywall.quota.title");
    description = t("paywall.quota." + (quotaKind || "text"));
  } else if (variant === "voice_cap") {
    title = t("paywall.voiceCap.title");
    description = t("paywall.voiceCap.description");
  }

  // Dominant CTA label: daily cap sells the subscription explicitly
  // ("Assinar Premium"); everything else keeps the existing labels.
  const primaryCtaLabel = isDailyQuota
    ? t("paywall.quota.daily.cta")
    : isNativeIapEnabled()
      ? t("pricing.core.cta")
      : t("paywall.moduleLocked.cta.unlock");

  // Outcome split: any close path that is NOT a CTA counts as a dismissal.
  const handleDismiss = () => {
    analytics.paywallDismissed(analyticsPlacement);
    onClose();
  };

  const handleUnlock = () => {
    analytics.paywallCtaClick(analyticsPlacement, "premium");
    onClose();
    // Carry the placement into the checkout funnel: Pricing reads it from
    // location.state and stamps checkout_started.source with it, closing the
    // cta_click → checkout_started attribution gap (absent = organic entry).
    navigate("/pricing", { state: { paywallPlacement: analyticsPlacement } });
  };

  const sheet = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
          style={{ background: "rgba(2, 6, 14, 0.82)", backdropFilter: "blur(12px)" }}
          onClick={handleDismiss}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="paywall-title"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:w-auto sm:min-w-[400px] sm:max-w-md rounded-t-3xl sm:rounded-3xl ring-1 ring-cyan-500/20 overflow-hidden max-h-[85dvh] overflow-y-auto"
            style={{
              background:
                "linear-gradient(180deg, #050811 0%, #0a0e1a 60%, #050811 100%)",
              boxShadow: "0 -10px 60px rgba(0,0,0,0.55)",
            }}
          >
            <button
              type="button"
              onClick={handleDismiss}
              aria-label={t("paywall.moduleLocked.cta.close")}
              className="absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-white/[0.06] hover:bg-white/[0.10] transition-colors"
            >
              <X size={16} className="text-foreground/70" />
            </button>

            <div
              className="px-6 pt-9 flex flex-col items-center text-center"
              style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom, 1.5rem))" }}
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                style={{
                  background:
                    "radial-gradient(circle at center, hsl(187 72% 50% / 0.22) 0%, hsl(187 72% 30% / 0.10) 70%)",
                  border: "1px solid hsl(187 72% 50% / 0.30)",
                }}
              >
                <Lock size={22} className="text-primary" />
              </div>

              <h2
                id="paywall-title"
                className="font-display text-[22px] font-semibold tracking-tight text-foreground leading-snug"
              >
                {title}
              </h2>

              <p className="font-display text-[14px] text-muted-foreground leading-relaxed mt-3 max-w-sm">
                {description}
              </p>

              {!isPaymentUiBlocked() && !livePremiumOut && (
                <p className="font-display text-[12px] tracking-wide text-foreground/80 mt-5">
                  {t("paywall.moduleLocked.price")}
                </p>
              )}

              {/* Credit balance — always shown (read-only, not a purchase
                  affordance). Lets the user know credits exist as an option. */}
              {creditBalanceUnits > 0 && (
                <p className="font-display text-[12px] text-foreground/55 mt-2">
                  {tf("credits.balance.short", { credits: String(unitsToCredits(creditBalanceUnits)) })}
                </p>
              )}

              <div className="w-full flex flex-col gap-2 mt-6">
                {livePremiumOut ? null : isNativeIapEnabled() ? (
                  // iOS native IAP path — StoreKit. Premium (subscription) AND a
                  // credit top-up (consumable, no entitlement → Free can buy too).
                  <>
                    <Button
                      variant="hero"
                      size="xl"
                      className="w-full"
                      onClick={() => {
                        analytics.paywallCtaClick(analyticsPlacement, "premium_iap");
                        setIapOpen(true);
                      }}
                    >
                      {primaryCtaLabel}
                    </Button>
                    {creditsApply && (
                      <Button
                        variant="outline"
                        size="lg"
                        className="w-full"
                        onClick={() => {
                          analytics.paywallCtaClick(analyticsPlacement, "credits_iap");
                          setCreditOpen(true);
                        }}
                      >
                        {t("credits.cta.buy")}
                      </Button>
                    )}
                  </>
                ) : isPaymentUiBlocked() ? (
                  <div className="w-full flex flex-col items-center gap-2 py-2">
                    <p className="font-display text-[13px] font-semibold text-foreground">
                      {t("ios.paymentBlocked.premiumOnly")}
                    </p>
                    <p className="font-display text-[12px] text-muted-foreground/70 text-center leading-relaxed">
                      {t("ios.paymentBlocked.webCta")}
                    </p>
                  </div>
                ) : (
                  <>
                    <Button
                      variant="hero"
                      size="xl"
                      className="w-full"
                      onClick={handleUnlock}
                    >
                      {primaryCtaLabel}
                    </Button>
                    {/* Secondary path: top up credits instead of upgrading.
                        Web/Android only — gated by isPaymentUiBlocked() above.
                        Nunca para o LIVE (crédito não cobre Realtime). */}
                    {creditsApply && (
                      <Button
                        variant="outline"
                        size="lg"
                        className="w-full"
                        onClick={() => {
                          analytics.paywallCtaClick(analyticsPlacement, "credits");
                          onClose();
                          // Same attribution handoff as the premium CTA above.
                          navigate("/creditos", { state: { paywallPlacement: analyticsPlacement } });
                        }}
                      >
                        {t("credits.cta.buy")}
                      </Button>
                    )}
                  </>
                )}
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full"
                  onClick={handleDismiss}
                >
                  {t("paywall.moduleLocked.cta.close")}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {sheet}
      {/* iOS StoreKit sheets — opened from the IAP CTAs. Siblings (not nested)
          so their backdrops don't bubble into the sheet's onClose. */}
      <IosPaywall open={iapOpen} onClose={() => setIapOpen(false)} />
      <IosCreditSheet
        open={creditOpen}
        onClose={() => setCreditOpen(false)}
        onPurchased={refreshCredits}
      />
    </>
  );
}

/**
 * Tiny loader-only placeholder export so deferred Phase-2 surfaces that
 * import PaywallSheet without finalized props don't have to redeclare
 * a busy state. Currently unused; safe to delete if v3.2 Phase 2 lands
 * without consuming it.
 */
export function PaywallLoading() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-md">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>
  );
}

export default PaywallSheet;
